/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchConnectedUser,
  fetchMyOrders,
  updateOrderStatus,
} from "../../../services/OrdersService";
import { toast } from "react-toastify";
import socket from "../../../services/socket";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../../utils/currency";
import "./OrdersBoard.css";

/** Board columns (API still uses prepared / rejected; UI shows 4 lanes only). */
const COLUMNS = [
  { key: "new", statusMatch: ["in progress"], labelKey: "newOrder" },
  { key: "accepted", statusMatch: ["accepted", "prepared"], labelKey: "accepted" },
  { key: "delivered", statusMatch: ["delivered"], labelKey: "delivered" },
  { key: "cancelled", statusMatch: ["rejected"], labelKey: "ordersColCancelled" },
];

function orderBelongsToColumn(order, col) {
  return col.statusMatch.includes(order.status);
}

function canDropOnColumn(order, targetKey) {
  const s = order.status;
  if (targetKey === "new") return false;
  if (targetKey === "accepted") return s === "in progress";
  if (targetKey === "delivered") return s === "accepted" || s === "prepared";
  if (targetKey === "cancelled")
    return s === "in progress" || s === "accepted" || s === "prepared";
  return false;
}

function filterOrdersForDisplay(data) {
  if (!Array.isArray(data)) return [];
  return data.filter((order) => {
    if (order.payMethod === "credit card") {
      return !!order.payerName;
    }
    return true;
  });
}

function nextApiStatuses(order) {
  const s = order.status;
  if (s === "in progress") return ["accepted", "rejected"];
  if (s === "accepted" || s === "prepared") return ["delivered", "rejected"];
  return [];
}

function labelForQuickAction(status, t) {
  if (status === "rejected") return t("ordersColCancelled");
  if (status === "accepted") return t("accepted");
  if (status === "delivered") return t("delivered");
  return status;
}

function getBoardLang() {
  let raw = "en";
  if (typeof localStorage !== "undefined") {
    raw = localStorage.getItem("currentLanguage") || "en";
  }
  return String(raw).toLowerCase().trim();
}

function productLineTitle(p) {
  const lang = getBoardLang();
  const details = p.product?.productDetails?.find(
    (d) => (d.language || "").toLowerCase().trim() === lang
  );
  return (
    details?.productTitle ||
    p.product?.productDetails?.[0]?.productTitle ||
    "—"
  );
}

function selectedItemLabel(si) {
  if (si.title) return si.title;
  const lang = getBoardLang();
  const details =
    si.item?.itemDetails?.find(
      (d) => (d.language || "").toLowerCase().trim() === lang
    ) || si.item?.itemDetails?.[0];
  return details?.itemTitle || "—";
}

function orderLineSubtotal(p) {
  const qty = p.quantity || 1;
  const base = (p.price || 0) * qty;
  const extras = (p.selectedItems || []).reduce((acc, s) => {
    if (s.isFree) return acc;
    const add = s.havePrice && s.price > 0 ? s.price : 0;
    return acc + add;
  }, 0);
  return base + extras * qty;
}

function statusLabelKey(status) {
  return (
    {
      "in progress": "newOrder",
      accepted: "accepted",
      rejected: "ordersColCancelled",
      prepared: "prepared",
      delivered: "delivered",
    }[status] || "pending"
  );
}

const OrdersBoard = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [prepModal, setPrepModal] = useState({ open: false, order: null, minutes: "15" });
  const [rejectModal, setRejectModal] = useState({ open: false, order: null, reason: "" });
  const [detailOrder, setDetailOrder] = useState(null);
  const restaurantIdRef = useRef(null);

  const refreshOrders = useCallback(async () => {
    try {
      const data = await fetchMyOrders();
      const filtered = filterOrdersForDisplay(data);
      filtered.sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
      setOrders(filtered);
    } catch (e) {
      console.error(e);
      toast.error(t("errorLoadingOrders") || "Could not load orders");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let active = true;

    const joinRestaurantRoom = () => {
      const id = restaurantIdRef.current;
      if (id != null && id !== "") {
        socket.emit("join-restaurant", id);
      }
    };

    const onNew = (order) => {
      if (!active || !order) return;
      const rid = order.restaurantId?._id || order.restaurantId;
      const mine = restaurantIdRef.current;
      if (mine != null && mine !== "" && String(rid) !== String(mine)) return;
      setOrders((prev) => {
        if (prev.some((o) => o.orderNumber === order.orderNumber)) return prev;
        const next = filterOrdersForDisplay([order]);
        if (!next.length) return prev;
        const merged = [order, ...prev];
        merged.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
        );
        return merged;
      });
    };

    const onUpdated = (updated) => {
      if (!active || !updated?.orderNumber) return;
      const rid = updated.restaurantId?._id || updated.restaurantId;
      const mine = restaurantIdRef.current;
      if (mine != null && mine !== "" && String(rid) !== String(mine)) return;
      setOrders((prev) => {
        const exists = prev.some((o) => o.orderNumber === updated.orderNumber);
        let next;
        if (exists) {
          next = prev.map((o) =>
            o.orderNumber === updated.orderNumber ? { ...o, ...updated } : o
          );
        } else {
          const allowed = filterOrdersForDisplay([updated]);
          next = allowed.length ? [updated, ...prev] : prev;
        }
        return [...next].sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
        );
      });
    };

    const init = async () => {
      await refreshOrders();
      if (!active) return;
      try {
        const user = await fetchConnectedUser();
        restaurantIdRef.current = user?.restaurantFK ?? null;
      } catch {
        restaurantIdRef.current = null;
      }
      if (!active) return;
      joinRestaurantRoom();
      socket.on("connect", joinRestaurantRoom);
      socket.on("new-order", onNew);
      socket.on("order-updated", onUpdated);
    };

    init();

    return () => {
      active = false;
      socket.off("connect", joinRestaurantRoom);
      socket.off("new-order", onNew);
      socket.off("order-updated", onUpdated);
    };
  }, [refreshOrders]);

  useEffect(() => {
    setDetailOrder((prev) => {
      if (!prev?.orderNumber) return prev;
      const fresh = orders.find((o) => o.orderNumber === prev.orderNumber);
      if (fresh) return fresh;
      return null;
    });
  }, [orders]);

  useEffect(() => {
    if (!detailOrder) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setDetailOrder(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detailOrder]);

  const applyStatusChange = async (order, newStatus, extra = {}) => {
    try {
      await updateOrderStatus(
        order.orderNumber,
        newStatus,
        extra.rejectReason ?? null,
        extra.estimatedPreparationTime ?? null
      );
      toast.success(t("statusUpdated") || "Order updated");
      await refreshOrders();
    } catch (err) {
      toast.error(err?.message || t("updateFailed") || "Update failed");
    }
  };

  const handleDropOnColumn = (targetKey, e) => {
    e.preventDefault();
    setDragOverCol(null);
    const raw = e.dataTransfer.getData("application/json");
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const { orderNumber, status: fromStatus } = payload;
    const order = orders.find((o) => o.orderNumber === orderNumber);
    if (!order || order.status !== fromStatus) return;
    const col = COLUMNS.find((c) => c.key === targetKey);
    if (!col) return;
    if (orderBelongsToColumn(order, col)) return;
    if (!canDropOnColumn(order, targetKey)) {
      toast.warning(t("invalidMove") || "This move is not allowed");
      return;
    }
    if (targetKey === "accepted") {
      setPrepModal({ open: true, order, minutes: "15" });
      return;
    }
    if (targetKey === "cancelled") {
      setRejectModal({ open: true, order, reason: "" });
      return;
    }
    if (targetKey === "delivered") {
      applyStatusChange(order, "delivered");
    }
  };

  const confirmPrep = async () => {
    const { order, minutes } = prepModal;
    if (!order) return;
    const tmin = parseInt(minutes, 10);
    if (Number.isNaN(tmin) || tmin <= 0) {
      toast.error(t("warningPreparationTimeInput") || "Enter a valid time");
      return;
    }
    setPrepModal({ open: false, order: null, minutes: "15" });
    await applyStatusChange(order, "accepted", { estimatedPreparationTime: tmin });
  };

  const confirmReject = async () => {
    const { order, reason } = rejectModal;
    if (!order) return;
    if (!reason.trim()) {
      toast.error(t("rejectReason") || "Please enter a reason");
      return;
    }
    setRejectModal({ open: false, order: null, reason: "" });
    await applyStatusChange(order, "rejected", { rejectReason: reason.trim() });
  };

  const quickMove = (order, newStatus) => {
    if (!nextApiStatuses(order).includes(newStatus)) {
      toast.warning(t("invalidMove") || "This move is not allowed");
      return;
    }
    if (newStatus === "accepted") {
      setPrepModal({ open: true, order, minutes: "15" });
      return;
    }
    if (newStatus === "rejected") {
      setRejectModal({ open: true, order, reason: "" });
      return;
    }
    applyStatusChange(order, newStatus);
  };

  const ordersInColumn = (col) => orders.filter((o) => orderBelongsToColumn(o, col));

  const nextActions = (order) => nextApiStatuses(order);

  if (loading) {
    return <div className="orders-board-page">{t("loading")}…</div>;
  }

  return (
    <div className="orders-board-page">
      <div className="orders-board-header">
        <div>
          <h1 className="orders-board-title">{t("ordersBoard")}</h1>
          <p className="orders-board-sub">
            {t("ordersBoardHint")}
          </p>
        </div>
        <div className="orders-board-header-actions">
          <Link to="/orders" className="btn btn-outline-primary btn-sm orders-board-nav-btn">
            <i className="fas fa-list me-2" aria-hidden />
            {t("ordersListView")}
          </Link>
        </div>
      </div>

      <div className="orders-board-columns">
        {COLUMNS.map((col) => (
          <div
            key={col.key}
            className={`orders-board-column orders-board-column--${col.key}`}
          >
            <div className="orders-board-column-header">
              <span>{t(col.labelKey)}</span>
              <span className="orders-board-count">{ordersInColumn(col).length}</span>
            </div>
            <div
              className={`orders-board-column-body ${dragOverCol === col.key ? "drag-over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverCol(col.key);
              }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => handleDropOnColumn(col.key, e)}
            >
              {ordersInColumn(col).length === 0 && (
                <div className="orders-board-empty-hint">
                  {t("dropOrdersHere") || "Drop orders here"}
                </div>
              )}
              {ordersInColumn(col).map((order) => (
                <div
                  key={order.orderNumber}
                  className="orders-board-card"
                  draggable
                  role="button"
                  tabIndex={0}
                  onClick={() => setDetailOrder(order)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setDetailOrder(order);
                    }
                  }}
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "application/json",
                      JSON.stringify({
                        orderNumber: order.orderNumber,
                        status: order.status,
                      })
                    );
                    e.dataTransfer.effectAllowed = "move";
                  }}
                >
                  <div className="orders-board-card-num">
                    {order.orderNumber}
                    {order.status === "prepared" && (
                      <span className="orders-board-card-badge">{t("prepared")}</span>
                    )}
                  </div>
                  <div className="orders-board-card-meta">
                    <div>
                      <strong>{t("customer") || "Customer"}:</strong>{" "}
                      {order.payerName?.trim() || "—"}
                    </div>
                    <div>
                      <strong>{t("tableNb") || "Table"}:</strong>{" "}
                      {order.tableNumber != null ? order.tableNumber : "—"}
                    </div>
                    <div>
                      <strong>{t("amount") || "Total"}:</strong>{" "}
                      {formatMoney(order.totalPrice)}
                    </div>
                    <div>
                      <strong>{t("date")}:</strong>{" "}
                      {order.createdAt
                        ? new Date(order.createdAt).toLocaleString()
                        : "—"}
                    </div>
                  </div>
                  {nextActions(order).length > 0 && (
                    <div
                      className="orders-board-card-actions"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {nextActions(order).map((st) => (
                        <button
                          key={st}
                          type="button"
                          className={st === "accepted" ? "primary" : ""}
                          onClick={() => quickMove(order, st)}
                        >
                          → {labelForQuickAction(st, t)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {detailOrder && (
        <div
          className="orders-board-detail-backdrop"
          role="presentation"
          onClick={() => setDetailOrder(null)}
        >
          <div
            className="orders-board-detail-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orders-board-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="orders-board-detail-head">
              <h2 id="orders-board-detail-title" className="orders-board-detail-title">
                {detailOrder.orderNumber}
              </h2>
              <button
                type="button"
                className="orders-board-detail-close"
                aria-label={t("close")}
                onClick={() => setDetailOrder(null)}
              >
                ×
              </button>
            </div>

            <div className="orders-board-detail-summary">
              <div>
                <span className="orders-board-detail-label">{t("statusOrder")}</span>
                <span className="orders-board-detail-value">
                  {t(statusLabelKey(detailOrder.status))}
                </span>
              </div>
              <div>
                <span className="orders-board-detail-label">{t("customer")}</span>
                <span className="orders-board-detail-value">
                  {detailOrder.payerName?.trim() || "—"}
                </span>
              </div>
              <div>
                <span className="orders-board-detail-label">{t("tableNb")}</span>
                <span className="orders-board-detail-value">
                  {detailOrder.tableNumber != null ? detailOrder.tableNumber : "—"}
                </span>
              </div>
              <div>
                <span className="orders-board-detail-label">{t("date")}</span>
                <span className="orders-board-detail-value">
                  {detailOrder.createdAt
                    ? new Date(detailOrder.createdAt).toLocaleString()
                    : "—"}
                </span>
              </div>
              <div>
                <span className="orders-board-detail-label">{t("payment")}</span>
                <span className="orders-board-detail-value">{detailOrder.payMethod}</span>
              </div>
              <div>
                <span className="orders-board-detail-label">{t("amount")}</span>
                <span className="orders-board-detail-value orders-board-detail-total">
                  {formatMoney(detailOrder.totalPrice)}
                </span>
              </div>
              {detailOrder.estimatedPreparationTime != null &&
                detailOrder.estimatedPreparationTime > 0 && (
                  <div>
                    <span className="orders-board-detail-label">{t("prepTime")}</span>
                    <span className="orders-board-detail-value">
                      {detailOrder.estimatedPreparationTime} min
                    </span>
                  </div>
                )}
              {detailOrder.status === "rejected" && detailOrder.rejectReason && (
                <div className="orders-board-detail-reason">
                  <span className="orders-board-detail-label">{t("rejectReason")}</span>
                  <span className="orders-board-detail-value">{detailOrder.rejectReason}</span>
                </div>
              )}
            </div>

            <h3 className="orders-board-detail-section-title">{t("products")}</h3>
            <div className="orders-board-detail-lines">
              {Array.isArray(detailOrder.products) && detailOrder.products.length > 0 ? (
                detailOrder.products.map((p, i) => (
                  <div key={i} className="orders-board-detail-line">
                    <div className="orders-board-detail-line-head">
                      {p.size ? (
                        <span className="orders-board-detail-size">{p.size}</span>
                      ) : null}
                      <span className="orders-board-detail-prod-title">
                        {productLineTitle(p)}
                      </span>
                      <span className="orders-board-detail-qty">×{p.quantity}</span>
                    </div>
                    <div className="orders-board-detail-line-prices">
                      <span>
                        {t("amount")}: {formatMoney(orderLineSubtotal(p))}
                      </span>
                      {p.price != null ? (
                        <span className="orders-board-detail-muted">
                          ({formatMoney(p.price)} × {p.quantity})
                        </span>
                      ) : null}
                    </div>
                    {p.selectedItems?.length > 0 ? (
                      <div className="orders-board-detail-items-wrap">
                        <div className="orders-board-detail-items-label">{t("items")}</div>
                        <ul className="orders-board-detail-items">
                          {p.selectedItems.map((si, j) => (
                            <li key={j}>
                              <span>{selectedItemLabel(si)}</span>
                              {si.havePrice && si.price > 0 && !si.isFree ? (
                                <span className="orders-board-detail-item-extra">
                                  +{formatMoney(si.price)}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <p className="orders-board-detail-empty">—</p>
              )}
            </div>
          </div>
        </div>
      )}

      {prepModal.open && prepModal.order && (
        <div
          className="modal fade show d-block orders-board-modal"
          style={{ background: "rgba(0,0,0,0.45)" }}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("setPreparationTime") || "Preparation time (minutes)"}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPrepModal({ open: false, order: null, minutes: "15" })}
                />
              </div>
              <div className="modal-body">
                <p>{prepModal.order.orderNumber}</p>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={prepModal.minutes}
                  onChange={(e) =>
                    setPrepModal((p) => ({ ...p, minutes: e.target.value }))
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPrepModal({ open: false, order: null, minutes: "15" })}
                >
                  {t("cancel")}
                </button>
                <button type="button" className="btn btn-primary" onClick={confirmPrep}>
                  {t("accept")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectModal.open && rejectModal.order && (
        <div
          className="modal fade show d-block orders-board-modal"
          style={{ background: "rgba(0,0,0,0.45)" }}
          role="dialog"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("rejectOrder") || "Reject order"}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setRejectModal({ open: false, order: null, reason: "" })}
                />
              </div>
              <div className="modal-body">
                <p>{rejectModal.order.orderNumber}</p>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder={t("rejectInput") || "Reason"}
                  value={rejectModal.reason}
                  onChange={(e) =>
                    setRejectModal((p) => ({ ...p, reason: e.target.value }))
                  }
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setRejectModal({ open: false, order: null, reason: "" })}
                >
                  {t("cancel")}
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmReject}>
                  {t("reject")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersBoard;
